import { constantRoutes } from '@/router'

const Layout = () => import('@/layout/index.vue')
const viewModules = require.context('@/views', true, /\.vue$/)

// ─────────────────────────────────────────────
// 路径工具
// ─────────────────────────────────────────────

/**
 * 将 routePath 与 parentPath 合并成最终路径。
 * - 绝对路径直接返回
 * - 相对路径拼接到 parentPath 后面
 */
function normalizePath(parentPath, routePath = '') {
  if (!routePath) return parentPath || '/'
  if (routePath.startsWith('/')) return routePath
  if (!parentPath || parentPath === '/') return `/${routePath}`.replace(/\/+/g, '/')
  return `${parentPath}/${routePath}`.replace(/\/+/g, '/')
}

/**
 * 递归收集 constantRoutes 里所有路由的完整绝对路径，
 * 用于在动态生成路由时跳过已有的常量路由，避免重复。
 */
function collectConstantPaths(routes, parentPath = '') {
  return routes.reduce((acc, route) => {
    const fullPath = normalizePath(parentPath, route.path)
    acc.push(fullPath)
    if (route.children && route.children.length) {
      acc.push(...collectConstantPaths(route.children, fullPath))
    }
    return acc
  }, [])
}

// ─────────────────────────────────────────────
// 动态路由生成
// ─────────────────────────────────────────────

function loadView(component) {
  // 优先匹配 component.vue，找不到时尝试 component/index.vue（目录名）
  const directPath = `./${component}.vue`
  const indexPath  = `./${component}/index.vue`

  const keys = viewModules.keys()
  const fullPath = keys.includes(directPath)
    ? directPath
    : keys.includes(indexPath)
      ? indexPath
      : null

  if (!fullPath) {
    console.warn(`[permission] 找不到视图组件: ${component}`)
    return () => import('@/views/error/404.vue')
  }
  return () => Promise.resolve(viewModules(fullPath)).then(mod => mod.default || mod)
}

/**
 * 根据后端菜单树生成 Vue Router 路由配置。
 * @param {Array}  menus       - 服务端菜单树（仅 type=1 的菜单节点会被处理）
 * @param {string} parentPath  - 当前递归层级的父路径
 * @param {Array}  skipPaths   - 已存在于 constantRoutes 中的路径，需跳过
 */
function generateRoutes(menus, parentPath = '', skipPaths = []) {
  const routes = []

  menus.forEach(menu => {
    // 只处理菜单类型（type=1），跳过按钮/权限节点（type=2）
    if (menu.type !== 1) return

    const currentPath = normalizePath(parentPath, menu.path)

    // 跳过与 constantRoutes 重复的路径（例如 /dashboard 已在常量路由中）
    if (skipPaths.includes(currentPath)) return

    const route = {
      path: currentPath,
      name: menu.name || undefined,
      meta: {
        title: menu.title,
        icon: menu.icon,
        menuId: menu.id
      }
    }

    if (menu.hidden) route.hidden = true

    // component === 'Layout'：显式容器菜单
    // component 以 '/' 开头（如 '/agreement/manage'）：数据库录入错误，但语义上仍是容器菜单，降级为 Layout
    const isLayoutContainer =
      menu.component === 'Layout' ||
      (menu.component && menu.component.startsWith('/'))

    if (isLayoutContainer) {
      route.component = Layout
      // 找到第一个 type=1 的子菜单作为默认重定向目标
      const firstMenuChild = (menu.children || []).find(c => c.type === 1)
      if (firstMenuChild) {
        route.redirect = normalizePath(currentPath, firstMenuChild.path)
      }
    } else if (menu.component) {
      route.component = loadView(menu.component)
    }

    // 递归生成子路由；只有非空时才挂载，避免空 children 干扰侧边栏渲染
    if (menu.children && menu.children.length) {
      const children = generateRoutes(menu.children, currentPath, skipPaths)
      if (children.length > 0) {
        route.children = children
      }
    }

    routes.push(route)
  })

  return routes
}

// ─────────────────────────────────────────────
// Vuex 模块
// ─────────────────────────────────────────────

const state = {
  routes: [],
  addRoutes: []
}

const mutations = {
  SET_ROUTES(state, routes) {
    state.addRoutes = routes
    state.routes = constantRoutes.concat(routes)
  }
}

const actions = {
  generateRoutes({ commit }, menus) {
    return new Promise(resolve => {
      const skipPaths = collectConstantPaths(constantRoutes)
      const accessRoutes = generateRoutes(menus, '', skipPaths)
      // 404 兜底路由放最后
      accessRoutes.push({ path: '*', redirect: '/404', hidden: true })
      commit('SET_ROUTES', accessRoutes)
      resolve(accessRoutes)
    })
  }
}

export default { namespaced: true, state, mutations, actions }
